<template>
  <div class="dropdown" ref="root" @keydown.esc.prevent="close" @keyup.esc.prevent="close">
    <button
      class="dropdown-toggle btn px-1"   :class="linkClass"
      type="button"
      :id="buttonId"
      :aria-expanded="isOpen.toString()"
      aria-haspopup="true"
      :aria-controls="menuId"
      @click="toggle"
      @keydown.down.prevent="openAndFocus(0)"
      @keydown.up.prevent="openAndFocus(items.length - 1)"
      ref="button"
    >
      <!-- Button label (slot optional). Default mirrors your two-span layout -->
      <slot name="button" :selected="selectedItem">
        <span class="d-none d-md-inline" v-html="selectedLabel"></span>
        <span class="d-inline d-md-none" v-html="selectedShortLabel"></span>
      </slot>
    </button>

    <!-- Teleport dropdown to body to avoid positioning context issues -->
   
    <ul
    v-if="isOpen"
    class="dropdown-menu position-fixed show"
    role="menu"
    :id="menuId"
    :aria-labelledby="buttonId"
    :style="dropdownStyle"
    @keydown.down.prevent="move(1)"
    @keydown.up.prevent="move(-1)"
    @keydown.enter.prevent="activateFocused"
    ref="menu"
    >
    <li v-for="(item, idx) in items" :key="getKey(item)" role="none" class="mb-1 p-1">
        <a class="dropdown-item py-2 px-2 rounded" 
            href="#" 
        :class="{ active: isSelected(item) }"
        @click="choose(item)"
        ref="itemBtns"
        @mousemove="focusedIndex = idx"
        >
            <slot name="item" :item="item" :selected="isSelected(item)">
                <span class="d-none d-md-inline" v-html="getLabel(item)"></span>
                <span class="d-inline d-md-none" v-html="getShortLabel(item)"></span>
            </slot>
        </a>
    </li>
    </ul>
    
  </div>
</template>

<script>
export default {
  name: "VcDropdown",
  props: {
    /* v-model */
    modelValue: { required: true },               // current selected id/value
    /* items list */
    items: { type: Array, required: true },       // [{ id, label, shortlabel }, ...] by default
    /* mapping props (override if your field names differ) */
    itemKey: { type: String, default: "id" },
    itemLabel: { type: String, default: "label" },
    itemShortLabel: { type: String, default: "shortlabel" },
     linkClass: {
        type: [String, Array, Object],
        default: ''
      }
  },
  data() {
    return {
      isOpen: false,
      focusedIndex: -1,
      buttonId: "btn-" + Math.random().toString(36).slice(2),
      menuId: "menu-" + Math.random().toString(36).slice(2),
      dropdownStyle: {},
     
    };
  },
  computed: {
    selectedItem() {
      return this.items.find(it => this.getKey(it) === this.modelValue) || null;
    },
    selectedLabel() {
      return this.selectedItem ? this.getLabel(this.selectedItem) : "";
    },
    selectedShortLabel() {
      return this.selectedItem ? this.getShortLabel(this.selectedItem) : "";
    },
  },
  methods: {
    getKey(item) {
      return item[this.itemKey];
    },
    getLabel(item) {
      return item[this.itemLabel] ?? "";
    },
    getShortLabel(item) {
      // fall back to full label if no short one
      return item[this.itemShortLabel] ?? this.getLabel(item);
    },
    isSelected(item) {
      return this.getKey(item) === this.modelValue;
    },

    calculatePosition() {
      if (this.$refs.button) {
        const rect = this.$refs.button.getBoundingClientRect();
        this.dropdownStyle = {
          top: `${rect.bottom}px`,
          left: `${rect.left}px`,
          zIndex: 1050,
          minWidth: `${rect.width}px`
        };
      }
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    },
    
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      
      // Calculate position when opening
      this.$nextTick(() => {
        this.calculatePosition();
        
        // focus currently selected item if any
        const idx = Math.max(
          0,
          this.items.findIndex(it => this.isSelected(it))
        );
        this.focusedIndex = idx;
        this.focusItem(idx);
      });
      this.$emit("open");
    },
    
    openAndFocus(idx) {
      this.open();
      this.$nextTick(() => {
        this.focusedIndex = idx;
        this.focusItem(idx);
      });
    },
    
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.focusedIndex = -1;
      this.$emit("close");
    },
    
    choose(item) {
      const val = this.getKey(item);
      if (val !== this.modelValue) {
        this.$emit("update:modelValue", val);
        this.$emit("change", val);
      }
      this.close();
    },
    
    move(delta) {
      if (!this.isOpen || !this.items.length) return;
      const len = this.items.length;
      this.focusedIndex = (this.focusedIndex + delta + len) % len;
      this.focusItem(this.focusedIndex);
    },
    
    activateFocused() {
      if (this.focusedIndex < 0) return;
      const item = this.items[this.focusedIndex];
      if (item) this.choose(item);
    },
    
    focusItem(idx) {
      const btns = this.$refs.itemBtns || [];
      const el = btns[idx];
      if (el && el.focus) el.focus();
    },
    
    onClickOutside(e) {
      const root = this.$refs.root;
      const menu = this.$refs.menu;
      if (this.isOpen && root && !root.contains(e.target) && 
          menu && !menu.contains(e.target)) {
        this.close();
      }
    },

    onScroll() {
      if (this.isOpen) {
        this.calculatePosition();
      }
    },

    onResize() {
      if (this.isOpen) {
        this.calculatePosition();
      }
    }
  },
  
  mounted() {
    document.addEventListener("click", this.onClickOutside);
    window.addEventListener("scroll", this.onScroll, true);
    window.addEventListener("resize", this.onResize);
  },
  
  beforeUnmount() {
    document.removeEventListener("click", this.onClickOutside);
    window.removeEventListener("scroll", this.onScroll, true);
    window.removeEventListener("resize", this.onResize);
  },
};
</script>

<style scoped>
/* Minimal styling since we're using teleport */
.dropdown {
  position: relative;
}

/* Style for teleported dropdown */
.dropdown-menu.position-fixed {
  position: fixed !important;
  transform: none !important;
}
</style>
